import { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';
import { store } from '../redux/store';
import { isTokenExpired } from '../utils/isTokenExpired';
import refreshTokenOrLogout from '../utils/refreshTokenOrLogout';
import { PostHog } from 'posthog-js';
import fetchWithRetry from './fetchWithRetry';
import { addLearnosityBffBreadcrumb, captureLearnosityException } from '../utils/learnositySentry';
import { LearnosityFetchError, readLearnosityResponse } from './LearnosityFetchError';

interface ResponseStatus {
  sessionId: string | null;
  status: string;
  message?: string;
}

function isResponseStatus(value: unknown): value is ResponseStatus {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const { sessionId, status, message } = value as Record<string, unknown>;
  return (
    (sessionId === null || typeof sessionId === 'string') &&
    typeof status === 'string' &&
    status.trim().length > 0 &&
    (message === undefined || typeof message === 'string')
  );
}

export const getAssessmentStatus = async (
  publicUserId: string,
  sessionId: string,
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
): Promise<ResponseStatus | undefined> => {
  const fields = { learnerId: publicUserId, sessionId };
  addLearnosityBffBreadcrumb('assessment_status', fields);

  try {
    let accessToken = store.getState().accessToken.value;
    if (isTokenExpired()) {
      accessToken = (await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog)) || null;
    }
    const response = await fetchWithRetry(
      `${
        import.meta.env.VITE_BFF_URL
      }/v1/learnosity/assessments/status?sessionId=${sessionId}&publicUserId=${publicUserId}`,
      {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    addLearnosityBffBreadcrumb('assessment_status', { ...fields, status: response.status });
    const data = await readLearnosityResponse(response, 'assessment_status', isResponseStatus);
    addLearnosityBffBreadcrumb('assessment_status', {
      ...fields,
      status: response.status,
      responseSessionId: data.sessionId,
      responseStatus: data.status,
      responseMessage: data.message,
    });
    return data;
  } catch (error) {
    captureLearnosityException(error, {
      operation: 'assessment_status',
      ...fields,
      ...(error instanceof LearnosityFetchError && { status: error.status }),
    });
    // Intentionally NOT re-thrown (unlike the other BFF calls): both callers use this
    // only as an optional pre-flight guard and must still be able to submit/save when
    // the status check can't complete after retries.
  }
};
